import { useEffect, useMemo, useState, useCallback } from 'react'
import { useTranslation } from 'react-i18next'
import { useLocation, useNavigate, useParams } from 'react-router-dom'
import { supabase } from './lib/supabase'
import { insertTradeComment } from './lib/tradeComments'
import {
  buildDailyCandlesFromMarketHistory,
  buildHistoryAnnotations,
  buildPositionRiskTimeline,
  buildStopTakeProfitSegments,
  buildTradeChartRows,
  fillDailyGaps,
  mergeYahooAndHistoryCandles,
  resolveChartRange,
  type TradeChartDayRow,
} from './lib/tradeChartData'
import { calcPositionRisk, calcPositionRiskPercent } from './lib/tradeCalculations'
import { fetchYahooDailyCandles } from './lib/yahooHistoricalChart'
import { updateTradeQuantityAfterEvent } from './lib/tradeClose'
import LocalizedDateTimeInput from './components/LocalizedDateTimeInput'
import TradeHistoryUpgradeCard from './components/TradeHistoryUpgradeCard'
import TradeDetailChart from './components/TradeDetailChart'
import TradeDetailStudio from './components/TradeDetailStudio'
import { buildStudioHistoryRows } from './lib/studioTradeHistory'
import { useMembership } from './hooks/useMembership'
import { useMarketDataRefreshInterval } from './hooks/useMarketDataRefreshInterval'
import { useAuth } from './contexts/AuthContext'
import { useNotification } from './contexts/NotificationContext'
import logo from '../traderhub24-logo.svg'
import SymbolLogo from './components/SymbolLogo'

type TradeDetailData = {
  symbol: string
  market: 'Commodity' | 'Crypto' | 'Forex' | 'Stock'
  trade_type: 'spot' | 'future'
  long_short: 'long' | 'short'
  quantity: number | null
  contract_size: number | null
  entry_date: string | null
  entry_price: number
  stop_loss: number
  take_profit: number
  rr: number | null
  exit_date: string | null
  exit_price: number | null
  trade_setup: string | null
  notes: string | null
  strategy: string | null
  strategy_id: string | null
  currency: string | null
  position_size: number | null
  pnl: number | null
  entry_setup_image: string | null
  exit_setup_image: string | null
  current_price?: number | null
  current_price_updated_at?: string | null
  previous_close_price?: number | null
  unrealized_pnl?: number | null
  unrealized_pnl_percentage?: number | null
  daily_change_percentage?: number | null
  pnl_percentage?: number | null
}

type TimelineEvent = {
  id?: string
  eventType?: string
  date: string
  title: string
  description: string
  price?: number
  mood?: string
  tag?: string
  quantity?: number | null
  metadata?: any
  autoGenerated?: boolean
}

type Comment = {
  author: string
  text: string
  date: string
  mood: string
}

type TradeDetailState = {
  trade?: TradeDetailData
  timeline?: TimelineEvent[]
  comments?: Comment[]
  mood?: string
  tradeId?: string
  historyAccessGranted?: boolean
  openAddEventWithType?: string
}

type TradeDetailFallback = TradeDetailState & {
  trade: TradeDetailData
  timeline: TimelineEvent[]
  comments: Comment[]
}

const createRandomId = () => {
  if (crypto.randomUUID) return crypto.randomUUID()
  return `detail-${Math.random().toString(36).slice(2, 10)}`
}

const formatDate = (dateString: string | null) => {
  if (!dateString) return '-'
  const date = new Date(dateString)
  return date.toLocaleString('en-US', {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  })
}

const formatNumber = (value: number | null, currency?: string | null, digits = 2) => {
  if (value === null || Number.isNaN(value)) return '-'
  const formatted = value.toFixed(digits).replace('.', ',')
  return currency ? `${formatted} ${currency}` : formatted
}

const formatShortDate = (dateString: string | null) => {
  if (!dateString) return '—'
  const date = new Date(dateString)
  return date.toLocaleString('en-US', {
    month: 'short',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  })
}

const buildFallbackData = (): TradeDetailFallback => {
  const basePrice = 142.3

  const timeline: TimelineEvent[] = [
    {
      date: new Date().toISOString(),
      title: 'Trade Opened',
      description: [
        '- Long Position',
        '- Symbol: AAPL (Stock)',
        '- Strategy: Trend continuation',
        '- Setup: Breakout + retest near 20 EMA with rising volume',
        `- Entry price: ${basePrice} USD`,
        `- Stop loss: ${(basePrice * 0.97).toFixed(2)} USD`,
        `- Take profit: ${(basePrice * 1.08).toFixed(2)} USD`,
        '- Risk/Reward ratio: 2.5:1.',
        `- Position size: 7120 USD`,
      ].join('\n'),
      price: basePrice,
      tag: 'entry',
    },
    {
      date: new Date(Date.now() + 24 * 60 * 60 * 1000).toISOString(),
      title: 'Macro Update',
      description: 'CPI print below expectations; risk-on flows strengthened.',
      tag: 'macro',
    },
    {
      date: new Date(Date.now() + 2 * 24 * 60 * 60 * 1000).toISOString(),
      title: 'Pullback Held',
      description: 'Price retested 20 EMA; held stop; maintained position size.',
      price: basePrice * 0.98,
      tag: 'discipline',
    },
    {
      date: new Date(Date.now() + 3 * 24 * 60 * 60 * 1000).toISOString(),
      title: 'Partial Take Profit',
      description: 'Scaled out 50% at 148.40; moved stop to breakeven.',
      price: basePrice * 1.04,
      tag: 'execution',
    },
    {
      date: new Date(Date.now() + 4 * 24 * 60 * 60 * 1000).toISOString(),
      title: 'Trade Closed',
      description: 'Exited remainder ahead of earnings; booked gains.',
      price: basePrice * 1.06,
      tag: 'exit',
    },
  ]

  const comments: Comment[] = []

  const trade: TradeDetailData = {
    symbol: 'AAPL',
    market: 'Stock',
    trade_type: 'spot',
    long_short: 'long',
    quantity: 50,
    contract_size: null,
    entry_date: new Date().toISOString(),
    entry_price: basePrice,
    stop_loss: basePrice * 0.97,
    take_profit: basePrice * 1.08,
    rr: 2.5,
    exit_date: new Date(Date.now() + 4 * 24 * 60 * 60 * 1000).toISOString(),
    exit_price: basePrice * 1.06,
    trade_setup: 'Breakout + retest near 20 EMA with rising volume',
    notes: 'Held through CPI; scaled out at key resistance; keep journaling emotions.',
    strategy: 'Trend continuation',
    strategy_id: null,
    currency: 'USD',
    position_size: 7120,
    pnl: 200,
    entry_setup_image: null,
    exit_setup_image: null,
  }

  return {
    trade,
    timeline,
    comments,
    tradeId: undefined,
    historyAccessGranted: undefined,
  }
}

function TradeDetail({ embedded, appearance = 'classic' }: { embedded?: boolean; appearance?: 'classic' | 'studio' } = {}) {
  const { detailId } = useParams()
  const studio = appearance === 'studio'
  const location = useLocation()
  const navigate = useNavigate()
  const state = (location.state as TradeDetailState | undefined) ?? {}
  const { isPro, isFree, isPublisher } = useMembership()
  const { showError, showSuccess } = useNotification()
  const { user } = useAuth()
  const { t } = useTranslation('trading')
  
  // Parse symbol from detailId if format is "SYMBOL-UUID"
  const parsedDetailId = detailId ? (detailId.includes('-') && detailId.split('-').length > 1 
    ? detailId.split('-').slice(1).join('-') // Extract UUID part
    : detailId) : null

  const fallback = useMemo(buildFallbackData, [])
  const trade = state.trade ?? fallback.trade
  
  const [history, setHistory] = useState<TimelineEvent[]>(state.timeline ?? [])
  const [historyLoading, setHistoryLoading] = useState(false)
  const [historyError, setHistoryError] = useState<string | null>(null)
  const [showAddEventModal, setShowAddEventModal] = useState(
    !!((location.state as TradeDetailState)?.openAddEventWithType)
  )
  const [showActionsMenu, setShowActionsMenu] = useState(false)
  const [showEditEventModal, setShowEditEventModal] = useState(false)
  const [editingEvent, setEditingEvent] = useState<TimelineEvent | null>(null)
  const [eventForm, setEventForm] = useState(() => {
    const presetType = (location.state as TradeDetailState)?.openAddEventWithType
    return {
      event_type: presetType || 'add',
      event_date: new Date().toISOString().slice(0, 16), // for <input type="datetime-local" />
      description: '',
      price: '',
      mood: '',
      quantity: '',
      exit_setup_image: '',
    }
  })
  const [submitting, setSubmitting] = useState(false)
  const [editSubmitting, setEditSubmitting] = useState(false)
  const [editEventForm, setEditEventForm] = useState({
    description: '',
    price: '',
    mood: '',
  })
  const [currentTrade, setCurrentTrade] = useState(trade)
  const [comments, setComments] = useState<Comment[]>(state.comments ?? [])
  const [commentsLoading, setCommentsLoading] = useState(false)
  const [showAddCommentModal, setShowAddCommentModal] = useState(false)
  const [commentForm, setCommentForm] = useState({
    text: '',
    mood: '',
  })
  const [commentSubmitting, setCommentSubmitting] = useState(false)
  const [selectedImage, setSelectedImage] = useState<string | null>(null)
  const [showImages, setShowImages] = useState(false)
  const [strategyInfo, setStrategyInfo] = useState<{ name: string; display_name: string } | null>(null)
  const [symbolInfo, setSymbolInfo] = useState<{ name: string | null; exchange: string | null }>({ name: null, exchange: null })
  const referenceId = parsedDetailId ?? createRandomId()
  const tradeId = state.tradeId

  const refreshTradeMarketData = useCallback(async () => {
    if (!tradeId || !user) return
    try {
      const { data, error } = await supabase
        .from('trades_with_market_data')
        .select('*')
        .eq('id', tradeId)
        .eq('user_id', user.id)
        .single()
      if (error || !data) return
      setCurrentTrade(data as TradeDetailData)
    } catch (err) {
      console.warn('Failed to refresh trade market data:', err)
    }
  }, [tradeId, user])

  useEffect(() => {
    refreshTradeMarketData()
  }, [refreshTradeMarketData])

  useMarketDataRefreshInterval(refreshTradeMarketData, Boolean(tradeId && user))

  const seededAllowHistory = (seed: string) => {
    let hash = 0
    for (let i = 0; i < seed.length; i++) {
      hash = (hash << 5) - hash + seed.charCodeAt(i)
      hash |= 0
    }
    const ratio = (hash >>> 0) / 4294967295
    return ratio < 0.2
  }

  const historyAllowed = useMemo(() => {
    if (isPro || isPublisher) return true
    if (state.historyAccessGranted !== undefined) return state.historyAccessGranted
    if (isFree) {
      return seededAllowHistory(referenceId)
    }
    return false
  }, [isPro, isPublisher, isFree, state.historyAccessGranted, referenceId])

  const averageCost = useMemo(() => {
    const t = currentTrade
    const qtyEvents = history.filter(
      e =>
        e.eventType === 'entry' ||
        e.eventType === 'add' ||
        e.eventType === 'scale_in' ||
        e.eventType === 'reduce_size' ||
        e.eventType === 'scale_out'
    )
    let cost = 0
    let qty = 0
    const currentQty = t.quantity ?? 0
    const sumAdd = qtyEvents
      .filter(e => e.eventType === 'add' || e.eventType === 'scale_in')
      .reduce((s, e) => s + (e.quantity ?? 0), 0)
    const sumReduce = qtyEvents
      .filter(e => e.eventType === 'reduce_size' || e.eventType === 'scale_out')
      .reduce((s, e) => s + (e.quantity ?? 0), 0)
    const hasEntryEvent = qtyEvents.some(e => e.eventType === 'entry')
    if (!hasEntryEvent && (sumAdd > 0 || sumReduce > 0)) {
      const initialQty = currentQty + sumReduce - sumAdd
      if (initialQty > 0) {
        cost = (t.entry_price ?? 0) * initialQty
        qty = initialQty
      }
    }
    for (const e of qtyEvents) {
      const eventQty = e.quantity ?? 0
      const price = e.price ?? t.entry_price ?? 0
      if (e.eventType === 'entry') {
        if (eventQty > 0) {
          cost = price * eventQty
          qty = eventQty
        }
      } else if (e.eventType === 'add' || e.eventType === 'scale_in') {
        if (eventQty > 0) {
          cost += price * eventQty
          qty += eventQty
        }
      } else if ((e.eventType === 'reduce_size' || e.eventType === 'scale_out') && eventQty > 0 && qty > 0) {
        const proportionalCost = (cost / qty) * Math.min(eventQty, qty)
        cost -= proportionalCost
        qty -= Math.min(eventQty, qty)
      }
    }
    return qty > 0 ? cost / qty : (t.entry_price ?? 0)
  }, [currentTrade, history])

  const riskTimeline = useMemo(
    () => buildPositionRiskTimeline(currentTrade, history),
    [currentTrade, history]
  )

  const chartAnnotations = useMemo(
    () => buildHistoryAnnotations(currentTrade, history, averageCost),
    [currentTrade, history, averageCost]
  )

  const latestRiskState = riskTimeline[riskTimeline.length - 1]
  const currentRiskAmount = latestRiskState?.riskAmount ?? calcPositionRisk(
    averageCost,
    currentTrade.stop_loss,
    currentTrade.quantity,
    currentTrade.contract_size,
    currentTrade.long_short
  )
  const currentRiskPct = latestRiskState?.riskPct ?? calcPositionRiskPercent(
    averageCost,
    currentTrade.stop_loss,
    currentTrade.quantity,
    currentTrade.contract_size,
    currentTrade.long_short,
    currentTrade.position_size
  )

  const [chartRows, setChartRows] = useState<TradeChartDayRow[]>([])
  const [chartLoading, setChartLoading] = useState(true)
  const [chartSource, setChartSource] = useState<'yahoo' | 'history' | 'mixed' | undefined>()

  const slTpSegments = useMemo(() => {
    const rangeEnd =
      chartRows.length > 0
        ? chartRows[chartRows.length - 1].time + 86400000
        : resolveChartRange(currentTrade).end
    return buildStopTakeProfitSegments(currentTrade, history, rangeEnd)
  }, [currentTrade, history, chartRows])

  useEffect(() => {
    let cancelled = false
    const loadChart = async () => {
      setChartLoading(true)
      const historyCandles = buildDailyCandlesFromMarketHistory(currentTrade, history, averageCost)
      const { start, end } = resolveChartRange(currentTrade)
      let candles = historyCandles
      let source: 'yahoo' | 'history' | 'mixed' = 'history'

      try {
        const yahoo = await fetchYahooDailyCandles(
          currentTrade.symbol,
          currentTrade.market,
          start,
          end,
          { currency: currentTrade.currency, exchange: symbolInfo.exchange }
        )
        if (yahoo.length) {
          candles = mergeYahooAndHistoryCandles(yahoo, historyCandles)
          source = historyCandles.length ? 'mixed' : 'yahoo'
        }
      } catch {
        // Yahoo unavailable (CORS/network) — use journal EOD snapshots
      }

      if (!candles.length) candles = historyCandles
      const priceSeed =
        currentTrade.previous_close_price ??
        currentTrade.entry_price ??
        candles[0]?.close ??
        null
      const filled = fillDailyGaps(candles, start, end, priceSeed)
      const rows = buildTradeChartRows(filled, riskTimeline, chartAnnotations)
      if (!cancelled) {
        setChartRows(rows)
        setChartSource(source)
        setChartLoading(false)
      }
    }
    void loadChart()
    return () => {
      cancelled = true
    }
  }, [currentTrade, history, averageCost, symbolInfo.exchange, riskTimeline, chartAnnotations])

  const slDistancePct = useMemo(() => {
    if (!averageCost || !currentTrade.stop_loss) return null
    return Math.abs((averageCost - currentTrade.stop_loss) / averageCost) * 100
  }, [averageCost, currentTrade.stop_loss])

  const realizedRR = useMemo(() => {
    const t = currentTrade
    const risk = Math.abs(averageCost - t.stop_loss)
    const reward = t.exit_price
      ? (t.long_short === 'long'
          ? t.exit_price - averageCost
          : averageCost - t.exit_price)
      : 0
    return risk > 0 && t.exit_price ? reward / risk : null
  }, [currentTrade, averageCost])

  const pnlPercentage = useMemo(() => {
    const t = currentTrade
    if (t.pnl_percentage != null) return t.pnl_percentage
    if (t.pnl != null && t.position_size != null && t.position_size !== 0) {
      return (t.pnl / t.position_size) * 100
    }
    return null
  }, [currentTrade])

  const historyTimelineVisible = useMemo(
    () =>
      history.filter(
        (e) => e.eventType !== 'market_data' && e.tag !== 'market_data'
      ),
    [history]
  )

  useEffect(() => {
    if (!tradeId) {
      setHistory(state.timeline ?? fallback.timeline)
      return
    }

    const fetchHistory = async () => {
      try {
        setHistoryLoading(true)
        const { data, error } = await supabase
          .from('trade_history')
          .select('id,event_type,description,price,mood,metadata,created_at')
          .eq('trade_id', tradeId)
          .order('created_at', { ascending: true })

        if (error) throw error

        if (data && data.length > 0) {
          const mapped: TimelineEvent[] = data.map((row: any) => {
            const metadata = row.metadata as any
            return {
              id: row.id,
              eventType: row.event_type,
              date: row.created_at,
              title: row.event_type.replace(/_/g, ' ').replace(/\b\w/g, (c: string) => c.toUpperCase()),
              description: row.description,
              price: row.price ?? undefined,
              mood: row.mood ?? undefined,
              tag: metadata?.tag ?? row.event_type,
              quantity: metadata?.quantity ?? null,
              metadata,
              autoGenerated: metadata?.auto_generated === true,
            }
          })
          setHistory(mapped)
        } else {
          setHistory(state.timeline ?? fallback.timeline)
        }
        setHistoryError(null)
      } catch (err: any) {
        setHistory(state.timeline ?? fallback.timeline)
        setHistoryError(err.message || 'Failed to load trade history')
      } finally {
        setHistoryLoading(false)
      }
    }

    fetchHistory()
  }, [tradeId, state.timeline, fallback.timeline])

  const openEditEvent = (evt: TimelineEvent) => {
    if (!evt.id) return
    if (evt.autoGenerated) return
    if (evt.eventType === 'market_data' || evt.tag === 'market_data') return
    setEditingEvent(evt)
    setEditEventForm({
      description: evt.description ?? '',
      price: evt.price !== undefined ? String(evt.price) : '',
      mood: evt.mood ?? '',
    })
    setShowEditEventModal(true)
  }

  const handleUpdateEvent = async (e: React.FormEvent) => {
    e.preventDefault()
    if (!tradeId || !user || !editingEvent?.id) {
      showError('Cannot update event: missing trade ID, user, or event')
      return
    }
    if (editingEvent.autoGenerated || editingEvent.eventType === 'market_data' || editingEvent.tag === 'market_data') {
      showError('This event is system-generated and cannot be edited')
      return
    }

    try {
      setEditSubmitting(true)
      const { error } = await supabase
        .from('trade_history')
        .update({
          description: editEventForm.description,
          price: editEventForm.price ? parseFloat(editEventForm.price) : null,
          mood: editEventForm.mood || null,
        })
        .eq('id', editingEvent.id)
        .eq('trade_id', tradeId)

      if (error) throw error

      // Refresh history after update
      const { data: historyData, error: historyError } = await supabase
        .from('trade_history')
        .select('id,event_type,description,price,mood,metadata,created_at')
        .eq('trade_id', tradeId)
        .order('created_at', { ascending: true })

      if (historyError) throw historyError

      if (historyData) {
        const mapped: TimelineEvent[] = historyData.map((row: any) => {
          const metadata = row.metadata as any
          return {
            id: row.id,
            eventType: row.event_type,
            date: row.created_at,
            title: row.event_type.replace(/_/g, ' ').replace(/\b\w/g, (c: string) => c.toUpperCase()),
            description: row.description,
            price: row.price ?? undefined,
            mood: row.mood ?? undefined,
            tag: metadata?.tag ?? row.event_type,
            quantity: metadata?.quantity ?? null,
            metadata,
            autoGenerated: metadata?.auto_generated === true,
          }
        })
        setHistory(mapped)
      }

      setShowEditEventModal(false)
      setEditingEvent(null)
    } catch (err: any) {
      showError(`Error updating event: ${err.message}`)
    } finally {
      setEditSubmitting(false)
    }
  }

  // Update currentTrade when trade data changes
  useEffect(() => {
    setCurrentTrade(trade)
  }, [trade])

  // Fetch strategy info if strategy_id exists
  useEffect(() => {
    const fetchStrategyInfo = async () => {
      const strategyId = trade.strategy_id || currentTrade.strategy_id
      if (!strategyId || !user) {
        setStrategyInfo(null)
        return
      }

      try {
        const { data, error } = await supabase
          .from('strategies')
          .select('name, display_name')
          .eq('id', strategyId)
          .single()

        if (error) throw error

        if (data) {
          setStrategyInfo({
            name: data.name,
            display_name: data.display_name,
          })
        } else {
          setStrategyInfo(null)
        }
      } catch (err: any) {
        console.error('Failed to fetch strategy info:', err)
        setStrategyInfo(null)
      }
    }

    fetchStrategyInfo()
  }, [trade.strategy_id, currentTrade.strategy_id, user])

  // Fetch symbols.name and symbols.exchange for trade symbol + market
  useEffect(() => {
    const fetchSymbolInfo = async () => {
      if (!trade.symbol || !trade.market) {
        setSymbolInfo({ name: null, exchange: null })
        return
      }
      try {
        const { data, error } = await supabase
          .from('symbols')
          .select('name, exchange')
          .eq('symbol', trade.symbol)
          .eq('market', trade.market)
          .limit(1)
          .maybeSingle()

        if (error) throw error
        if (data) {
          setSymbolInfo({
            name: data.name ?? null,
            exchange: data.exchange ?? null,
          })
        } else {
          setSymbolInfo({ name: null, exchange: null })
        }
      } catch (err: any) {
        console.error('Failed to fetch symbol info:', err)
        setSymbolInfo({ name: null, exchange: null })
      }
    }
    fetchSymbolInfo()
  }, [trade.symbol, trade.market])

  // Fetch comments from database
  useEffect(() => {
    if (!tradeId) {
      setComments(state.comments ?? [])
      return
    }

    const fetchComments = async () => {
      try {
        setCommentsLoading(true)
        const { data, error } = await supabase
          .from('comments')
          .select('id,text,mood,created_at')
          .eq('trade_id', tradeId)
          .order('created_at', { ascending: false })

        if (error) throw error

        if (data && data.length > 0) {
          const mapped: Comment[] = data.map((row) => ({
            author: 'You',
            text: row.text,
            date: row.created_at,
            mood: row.mood || 'Neutral',
          }))
          setComments(mapped)
        } else {
          setComments(state.comments ?? [])
        }
      } catch (err: any) {
        console.error('Failed to load comments:', err.message)
        setComments(state.comments ?? [])
      } finally {
        setCommentsLoading(false)
      }
    }

    fetchComments()
  }, [tradeId, state.comments])

  const handleAddEvent = async (e: React.FormEvent) => {
    e.preventDefault()
    if (!tradeId || !user) {
      showError('Cannot add event: missing trade ID or user')
      return
    }

    try {
      setSubmitting(true)

      if (eventForm.event_type === 'exit') {
        const exitDate = eventForm.event_date?.trim()
        const exitPrice = eventForm.price ? parseFloat(eventForm.price) : NaN
        if (!exitDate) {
          showError('Exit date is required')
          setSubmitting(false)
          return
        }
        if (!Number.isFinite(exitPrice) || exitPrice <= 0) {
          showError('Exit price is required and must be greater than 0')
          setSubmitting(false)
          return
        }
        const { data: updatedTrade, error } = await supabase
          .from('trades_with_market_data')
          .update({
            exit_date: new Date(exitDate).toISOString(),
            exit_price: exitPrice,
            exit_setup_image: eventForm.exit_setup_image?.trim() || null,
            quantity: 0,
            position_size: 0,
          })
          .eq('id', tradeId)
          .select()
          .single()

        if (error) throw error

        setCurrentTrade((prev) => ({ ...prev, ...updatedTrade }))

        const { data: historyData, error: historyError } = await supabase
          .from('trade_history')
          .select('id,event_type,description,price,mood,metadata,created_at')
          .eq('trade_id', tradeId)
          .order('created_at', { ascending: true })

        if (historyError) throw historyError

        if (historyData) {
          const mapped: TimelineEvent[] = historyData.map((row: any) => {
            const metadata = row.metadata as any
            return {
              id: row.id,
              eventType: row.event_type,
              date: row.created_at,
              title: row.event_type.replace(/_/g, ' ').replace(/\b\w/g, (c: string) => c.toUpperCase()),
              description: row.description,
              price: row.price ?? undefined,
              mood: row.mood ?? undefined,
              tag: metadata?.tag ?? row.event_type,
              quantity: metadata?.quantity ?? null,
              metadata,
              autoGenerated: metadata?.auto_generated === true,
            }
          })
          setHistory(mapped)
        }

        const pct = updatedTrade?.pnl_percentage != null
          ? `${updatedTrade.pnl_percentage >= 0 ? 'profit' : 'loss'} ${updatedTrade.pnl_percentage >= 0 ? '+' : ''}${Number(updatedTrade.pnl_percentage).toFixed(2)}%`
          : 'closed'
        const priceStr = formatMoneyForSentence(exitPrice, currentTrade.currency)
        const dateStr = formatDateForSentence(exitDate)
        showSuccess(`Position closed at ${priceStr} on ${dateStr} with ${pct}`)

        setEventForm({
          event_type: 'add',
          event_date: new Date().toISOString().slice(0, 16),
          description: '',
          price: '',
          mood: '',
          quantity: '',
          exit_setup_image: '',
        })
        setShowAddEventModal(false)
        setSubmitting(false)
        return
      }

      const isAddOrReduce = eventForm.event_type === 'add' || eventForm.event_type === 'reduce_size'
      if (isAddOrReduce) {
        const parsedPrice = eventForm.price ? parseFloat(eventForm.price) : NaN
        if (!Number.isFinite(parsedPrice)) {
          showError('Price is required for Add / Reduce Size events')
          setSubmitting(false)
          return
        }
      }
      const descriptionText = isAddOrReduce ? buildAutoDescriptionForAddReduce() : eventForm.description

      // Calculate new quantity and position size if quantity is provided
      let updatedQuantity = currentTrade.quantity ?? 0
      let updatedPositionSize = currentTrade.position_size ?? 0

      if (eventForm.quantity && (eventForm.event_type === 'add' || eventForm.event_type === 'reduce_size')) {
        const quantityChange = parseFloat(eventForm.quantity)
        
        if (eventForm.event_type === 'add') {
          // Add to position
          updatedQuantity = (currentTrade.quantity ?? 0) + quantityChange
          
          // Calculate new position size (quantity * price)
          const eventPrice = eventForm.price ? parseFloat(eventForm.price) : currentTrade.entry_price
          const addedPositionValue = quantityChange * eventPrice
          updatedPositionSize = (currentTrade.position_size ?? 0) + addedPositionValue
        } else if (eventForm.event_type === 'reduce_size') {
          // Reduce position
          updatedQuantity = (currentTrade.quantity ?? 0) - quantityChange
          
          // Calculate reduced position size (quantity * price)
          const eventPrice = eventForm.price ? parseFloat(eventForm.price) : currentTrade.entry_price
          const reducedPositionValue = quantityChange * eventPrice
          updatedPositionSize = (currentTrade.position_size ?? 0) - reducedPositionValue
        }

        // Make sure quantity doesn't go negative
        if (updatedQuantity < 0) {
          showError('Quantity cannot be negative')
          setSubmitting(false)
          return
        }
      }

      // Insert event into trade_history
      const { error } = await supabase
        .from('trade_history')
        .insert({
          trade_id: tradeId,
          user_id: user.id,
          event_type: eventForm.event_type,
          description: descriptionText,
          price: eventForm.price ? parseFloat(eventForm.price) : null,
          mood: eventForm.mood || null,
          metadata: { 
            tag: eventForm.event_type,
            quantity: eventForm.quantity ? parseFloat(eventForm.quantity) : null,
          },
          ...(isAddOrReduce && eventForm.event_date
            ? { created_at: new Date(eventForm.event_date).toISOString() }
            : {}),
        })
        .select()

      if (error) throw error

      // quantity/position_size/totals sync via DB trigger on history insert; only close trade when reduce hits zero.
      if (eventForm.quantity && (eventForm.event_type === 'add' || eventForm.event_type === 'reduce_size')) {
        const eventPrice = eventForm.price ? parseFloat(eventForm.price) : currentTrade.entry_price
        const fullyClosedByReduce = eventForm.event_type === 'reduce_size' && updatedQuantity === 0

        if (fullyClosedByReduce) {
          const exitDateIso = eventForm.event_date?.trim()
            ? new Date(eventForm.event_date).toISOString()
            : new Date().toISOString()

          const { data: closedTrade, error: updateError } = await updateTradeQuantityAfterEvent(
            supabase,
            tradeId,
            {
              eventType: eventForm.event_type,
              updatedQuantity,
              updatedPositionSize,
              exitDateIso,
              exitPrice: eventPrice,
            },
          )

          if (updateError) throw updateError

          if (closedTrade) {
            setCurrentTrade({
              ...currentTrade,
              quantity: 0,
              position_size: 0,
              exit_date: closedTrade.exit_date as string,
              exit_price: closedTrade.exit_price as number,
              pnl: closedTrade.pnl as number | null,
              pnl_percentage: closedTrade.pnl_percentage as number | null,
            })

            const pct =
              closedTrade.pnl_percentage != null
                ? `${Number(closedTrade.pnl_percentage) >= 0 ? 'profit' : 'loss'} ${Number(closedTrade.pnl_percentage) >= 0 ? '+' : ''}${Number(closedTrade.pnl_percentage).toFixed(2)}%`
                : 'closed'
            const priceStr = formatMoneyForSentence(eventPrice, currentTrade.currency)
            const dateStr = formatDateForSentence(exitDateIso)
            showSuccess(`Position closed at ${priceStr} on ${dateStr} with ${pct}`)
          }
        } else {
          await refreshTradeMarketData()
        }
      }

      // Refresh history
      const { data: historyData, error: historyError } = await supabase
        .from('trade_history')
        .select('id,event_type,description,price,mood,metadata,created_at')
        .eq('trade_id', tradeId)
        .order('created_at', { ascending: true })

      if (historyError) throw historyError

      if (historyData) {
        const mapped: TimelineEvent[] = historyData.map((row: any) => {
          const metadata = row.metadata as any
          return {
            id: row.id,
            eventType: row.event_type,
            date: row.created_at,
            title: row.event_type.replace(/_/g, ' ').replace(/\b\w/g, (c: string) => c.toUpperCase()),
            description: row.description,
            price: row.price ?? undefined,
            mood: row.mood ?? undefined,
            tag: metadata?.tag ?? row.event_type,
            quantity: metadata?.quantity ?? null,
            metadata,
            autoGenerated: metadata?.auto_generated === true,
          }
        })
        setHistory(mapped)
      }

      // Reset form
      setEventForm({
        event_type: 'add',
        event_date: new Date().toISOString().slice(0, 16),
        description: '',
        price: '',
        mood: '',
        quantity: '',
        exit_setup_image: '',
      })
      setShowAddEventModal(false)
    } catch (err: any) {
      showError(`Error adding event: ${err.message}`)
    } finally {
      setSubmitting(false)
    }
  }

  const eventTypes = [
    { value: 'add', label: 'Add' },
    { value: 'stop_move', label: 'Stop Move' },
    { value: 'breakeven_move', label: 'Breakeven Move' },
    { value: 'hedge', label: 'Hedge' },
    { value: 'reduce_size', label: 'Reduce Size' },
    { value: 'exit', label: 'Exit' },
    { value: 'note', label: 'Note' },
    { value: 'review', label: 'Review' },
    { value: 'mood_check', label: 'Mood Check' },
  ]

  const showQuantityField = eventForm.event_type === 'add' || eventForm.event_type === 'reduce_size'
  const showEventDateField = showQuantityField || eventForm.event_type === 'exit'
  const showExitFields = eventForm.event_type === 'exit'

  const getCurrencySymbol = (currency?: string | null) => {
    const c = (currency || '').toUpperCase()
    if (c === 'USD') return '$'
    if (c === 'EUR') return '€'
    if (c === 'GBP') return '£'
    if (c === 'TRY') return '₺'
    return null
  }

  const formatMoneyForSentence = (value: number, currency?: string | null) => {
    const numberText = value.toLocaleString('tr-TR', {
      minimumFractionDigits: 0,
      maximumFractionDigits: 8,
    })
    const symbol = getCurrencySymbol(currency)
    return symbol ? `${numberText}${symbol}` : `${numberText} ${currency ?? ''}`.trim()
  }

  const formatDateForSentence = (dateTimeLocal: string) => {
    const d = new Date(dateTimeLocal)
    return d.toLocaleDateString('tr-TR', { day: '2-digit', month: '2-digit', year: 'numeric' })
  }

  const buildAutoDescriptionForAddReduce = () => {
    const basePrice = eventForm.price ? parseFloat(eventForm.price) : NaN
    const action = eventForm.event_type === 'add' ? 'Added' : 'Reduced'
    const dateText = eventForm.event_date
      ? formatDateForSentence(eventForm.event_date)
      : formatDateForSentence(new Date().toISOString())
    if (!Number.isFinite(basePrice)) return `${action} on ${dateText}`
    return `${action} on ${dateText} at ${formatMoneyForSentence(basePrice, currentTrade.currency)}`
  }

  const buildExitDescription = () => {
    const exitPrice = eventForm.price ? parseFloat(eventForm.price) : NaN
    const dateText = eventForm.event_date
      ? formatDateForSentence(eventForm.event_date)
      : formatDateForSentence(new Date().toISOString())
    if (!Number.isFinite(exitPrice)) return `Position closed on ${dateText}`
    const priceStr = formatMoneyForSentence(exitPrice, currentTrade.currency)
    const qty = currentTrade.quantity ?? 0
    const entry = currentTrade.entry_price
    let pctStr = ''
    if (qty > 0 && entry > 0) {
      let pnl: number
      if (currentTrade.long_short === 'long') {
        pnl = (exitPrice - entry) * qty
      } else {
        pnl = (entry - exitPrice) * qty
      }
      const posSize = qty * entry
      const pct = posSize > 0 ? (pnl / posSize) * 100 : 0
      const sign = pct >= 0 ? '+' : ''
      pctStr = ` with ${pct >= 0 ? 'profit' : 'loss'} ${sign}${pct.toFixed(2)}%`
    }
    return `Position closed at ${priceStr} on ${dateText}${pctStr}`
  }

  // Check if trade is closed (has exit price or exit date)
  const isTradeClosed = currentTrade.exit_price !== null || currentTrade.exit_date !== null

  const stoppedOut = useMemo(() => {
    if (!isTradeClosed || currentTrade.exit_price == null || !currentTrade.stop_loss) return false
    return currentTrade.long_short === 'long'
      ? currentTrade.exit_price <= currentTrade.stop_loss
      : currentTrade.exit_price >= currentTrade.stop_loss
  }, [isTradeClosed, currentTrade.exit_price, currentTrade.stop_loss, currentTrade.long_short])

  const heldDays = useMemo(() => {
    if (!currentTrade.entry_date) return null
    const start = new Date(currentTrade.entry_date).getTime()
    const end = currentTrade.exit_date ? new Date(currentTrade.exit_date).getTime() : Date.now()
    return Math.max(0, Math.round((end - start) / 86400000))
  }, [currentTrade.entry_date, currentTrade.exit_date])

  const addCount = useMemo(
    () => history.filter((e) => e.eventType === 'add' || e.eventType === 'scale_in').length,
    [history]
  )

  const studioHistoryRows = useMemo(
    () =>
      buildStudioHistoryRows(historyTimelineVisible, {
        stopPrice: currentTrade.stop_loss,
        formatShortDate: (date) => formatShortDate(date),
      }),
    [historyTimelineVisible, currentTrade.stop_loss]
  )

  // Free users can still add/reduce/stop_move via Actions (no full history access)
  const canDoActions = Boolean(tradeId && user && !isTradeClosed)

  const openAddEventWithType = (eventType: 'add' | 'reduce_size' | 'stop_move' | 'exit') => {
    setEventForm((prev) => ({
      ...prev,
      event_type: eventType,
      event_date: new Date().toISOString().slice(0, 16),
      description: '',
      price: '',
      mood: '',
      quantity: '',
      exit_setup_image: '',
    }))
    setShowActionsMenu(false)
    setShowAddEventModal(true)
  }

  const handleAddComment = async (e: React.FormEvent) => {
    e.preventDefault()
    if (!tradeId || !user) {
      showError('Cannot add comment: missing trade ID or user')
      return
    }

    try {
      setCommentSubmitting(true)
      await insertTradeComment(supabase, {
        tradeId,
        userId: user.id,
        text: commentForm.text,
        mood: commentForm.mood || null,
      })

      // Refresh comments
      const { data: commentsData, error: commentsError } = await supabase
        .from('comments')
        .select('id,text,mood,created_at')
        .eq('trade_id', tradeId)
        .order('created_at', { ascending: false })

      if (commentsError) throw commentsError

      if (commentsData) {
        const mapped: Comment[] = commentsData.map((row) => ({
          author: 'You',
          text: row.text,
          date: row.created_at,
          mood: row.mood || 'Neutral',
        }))
        setComments(mapped)
      }

      // Reset form
      setCommentForm({
        text: '',
        mood: '',
      })
      setShowAddCommentModal(false)
    } catch (err: any) {
      showError(`Error adding comment: ${err.message}`)
    } finally {
      setCommentSubmitting(false)
    }
  }

  const rootClass = studio && embedded
    ? 'dashboard-page-content at-trade-detail-studio'
    : embedded
      ? 'dashboard-page-content'
      : 'min-h-screen bg-app-bg text-gray-900'

  return (
    <div className={rootClass}>
      {!embedded && (
        <header className="w-full py-6 px-6 max-w-6xl mx-auto">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <button
                onClick={() => navigate('/app')}
                className="flex-shrink-0"
              >
                <img src={logo} alt="TraderHub24" className="h-12 md:h-16 w-auto cursor-pointer hover:opacity-80 transition-opacity" />
              </button>
              <div>
                <h1 className="text-3xl font-bold">Trade Detail</h1>
                <p className="text-sm text-gray-500 mt-1">Reference: {referenceId}</p>
              </div>
            </div>
            <div className="flex items-center gap-3">
              <div className="flex items-center gap-2">
                <span
                  className={`px-3 py-1 rounded-full text-xs font-semibold ${
                    trade.long_short === 'long'
                      ? 'bg-green-100 text-green-700'
                      : 'bg-red-100 text-red-700'
                  }`}
                >
                  {trade.long_short.toUpperCase()}
                </span>
                <span className="px-3 py-1 rounded-full text-xs font-semibold bg-gray-100 text-gray-700">
                  {trade.market}
                </span>
                <span className="px-3 py-1 rounded-full text-xs font-semibold bg-blue-100 text-blue-700 capitalize">
                  {trade.trade_type}
                </span>
              </div>
              <button
                onClick={() => window.history.back()}
                className="inline-flex items-center gap-2 text-sm text-gray-600 hover:text-orange-600 transition-colors px-3 py-2 border border-gray-300 rounded-lg hover:bg-gray-50"
              >
                <span aria-hidden="true">←</span>
                Back
              </button>
            </div>
          </div>
        </header>
      )}

      {embedded && !studio && (
        <div className="mb-6">
          <div className="flex items-center justify-between mb-4">
            <div>
              <h1 className="text-2xl font-bold">Trade Detail</h1>
              <p className="text-sm text-gray-500 mt-1">Reference: {referenceId}</p>
            </div>
            <div className="flex items-center gap-2">
              <span
                className={`px-3 py-1 rounded-full text-xs font-semibold ${
                  trade.long_short === 'long'
                    ? 'bg-green-100 text-green-700'
                    : 'bg-red-100 text-red-700'
                }`}
              >
                {trade.long_short.toUpperCase()}
              </span>
              <span className="px-3 py-1 rounded-full text-xs font-semibold bg-gray-100 text-gray-700">
                {trade.market}
              </span>
              <span className="px-3 py-1 rounded-full text-xs font-semibold bg-blue-100 text-blue-700 capitalize">
                {trade.trade_type}
              </span>
            </div>
          </div>
        </div>
      )}

      <main className={embedded ? "w-full space-y-8" : "max-w-6xl mx-auto px-6 pb-16 space-y-8"}>
        {studio && embedded ? (
          <TradeDetailStudio
            trade={trade}
            currentTrade={currentTrade}
            symbolInfo={symbolInfo}
            strategyInfo={strategyInfo}
            averageCost={averageCost}
            slDistancePct={slDistancePct}
            pnlPercentage={pnlPercentage}
            realizedRR={realizedRR}
            currentRiskAmount={currentRiskAmount}
            currentRiskPct={currentRiskPct}
            heldDays={heldDays}
            addCount={addCount}
            isTradeClosed={isTradeClosed}
            stoppedOut={stoppedOut}
            paidMembership={isPro || isPublisher}
            chartRows={chartRows}
            chartAnnotations={chartAnnotations}
            slTpSegments={slTpSegments}
            chartLoading={chartLoading}
            chartSource={chartSource}
            historyAllowed={historyAllowed}
            historyLoading={historyLoading}
            historyError={historyError}
            studioHistoryRows={studioHistoryRows}
            historyEventCount={historyTimelineVisible.length}
            canDoActions={canDoActions}
            showActionsMenu={showActionsMenu}
            setShowActionsMenu={setShowActionsMenu}
            openAddEventWithType={openAddEventWithType}
            comments={comments}
            commentsLoading={commentsLoading}
            tradeId={tradeId}
            setShowAddCommentModal={setShowAddCommentModal}
            setShowAddEventModal={setShowAddEventModal}
            openEditEvent={openEditEvent}
            editSubmitting={editSubmitting}
            showImages={showImages}
            setShowImages={setShowImages}
            formatNumber={formatNumber}
            formatDate={formatDate}
            formatShortDate={formatShortDate}
          />
        ) : (
        <>
        <section className="bg-app-box border border-gray-200 rounded-2xl shadow-sm p-6">
          <div className="flex items-center justify-between">
            <div>
              <div className="flex flex-wrap items-center gap-x-2 gap-y-1">
                <SymbolLogo
                  symbol={trade.symbol}
                  market={trade.market}
                  exchange={symbolInfo.exchange}
                  size={studio ? 36 : 32}
                  className={studio ? 'at-symbol-avatar at-trade-detail-symbol-logo' : 'inline-flex h-8 w-8 items-center justify-center rounded-lg bg-gray-100 text-xs font-bold text-gray-600 overflow-hidden'}
                />
                <h2 className="text-2xl font-semibold text-gray-900">{trade.symbol}</h2>
                {(symbolInfo.name || symbolInfo.exchange) && (
                  <span className="text-sm text-gray-500">
                    {[symbolInfo.name, symbolInfo.exchange].filter(Boolean).join(' · ')}
                  </span>
                )}
              </div>
              {strategyInfo ? (
                <button
                  onClick={() => {
                    const encodedName = encodeURIComponent(strategyInfo.name)
                    navigate(studio ? '/app-test/strategies' : `/app/strategy/${encodedName}`)
                  }}
                  className="text-sm text-orange-600 hover:text-orange-700 mt-1 hover:underline transition-colors"
                >
                  {strategyInfo.display_name}
                </button>
              ) : (
                <p className="text-sm text-gray-500 mt-1">{trade.strategy || 'No strategy specified'}</p>
              )}
            </div>
            {canDoActions && (
              <div className="relative">
                <button
                  type="button"
                  onClick={() => setShowActionsMenu((v) => !v)}
                  className="px-3 py-1.5 bg-orange-600 hover:bg-orange-700 text-white text-sm font-medium rounded-lg transition-colors inline-flex items-center gap-1.5"
                >
                  Actions
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                  </svg>
                </button>
                {showActionsMenu && (
                  <>
                    <div
                      className="fixed inset-0 z-40"
                      aria-hidden="true"
                      onClick={() => setShowActionsMenu(false)}
                    />
                    <div className="absolute right-0 top-full mt-1 z-50 min-w-[160px] py-1 bg-white border border-gray-200 rounded-lg shadow-lg">
                      <button
                        type="button"
                        onClick={() => openAddEventWithType('add')}
                        className="w-full text-left px-3 py-2 text-sm text-gray-700 hover:bg-orange-50 hover:text-orange-700 transition-colors"
                      >
                        Add
                      </button>
                      <button
                        type="button"
                        onClick={() => openAddEventWithType('reduce_size')}
                        className="w-full text-left px-3 py-2 text-sm text-gray-700 hover:bg-orange-50 hover:text-orange-700 transition-colors"
                      >
                        Reduce
                      </button>
                      <button
                        type="button"
                        onClick={() => openAddEventWithType('stop_move')}
                        className="w-full text-left px-3 py-2 text-sm text-gray-700 hover:bg-orange-50 hover:text-orange-700 transition-colors"
                      >
                        Stop Move
                      </button>
                      <button
                        type="button"
                        onClick={() => openAddEventWithType('exit')}
                        className="w-full text-left px-3 py-2 text-sm text-gray-700 hover:bg-orange-50 hover:text-orange-700 transition-colors"
                      >
                        Exit
                      </button>
                    </div>
                  </>
                )}
              </div>
            )}
          </div>

          <div className="mt-6 border border-gray-200 rounded-xl p-4 md:p-5 bg-white">
            <h3 className="text-sm font-bold text-gray-900">{t('tradeDetail.metrics.title')}</h3>
            <p className="text-xs text-gray-500 mt-0.5 mb-4">{t('tradeDetail.metrics.subtitle')}</p>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
              <div className="rounded-lg border border-gray-100 bg-gray-50/80 p-3">
                <p className="text-[10px] font-semibold uppercase tracking-wide text-gray-500">{t('tradeDetail.metrics.entryPrice')}</p>
                <p className="text-lg font-bold text-gray-900 mt-1">{formatNumber(trade.entry_price, trade.currency)}</p>
                <p className="text-[11px] text-gray-500 mt-1">{formatDate(trade.entry_date)}</p>
              </div>
              <div className="rounded-lg border border-blue-100 bg-blue-50/60 p-3">
                <p className="text-[10px] font-semibold uppercase tracking-wide text-gray-500">{t('tradeDetail.metrics.avgCost')}</p>
                <p className="text-lg font-bold text-blue-800 mt-1">{formatNumber(averageCost, trade.currency)}</p>
                <p className="text-[11px] text-gray-500 mt-1">{t('tradeDetail.metrics.avgCostHint')}</p>
              </div>
              <div className="rounded-lg border border-red-100 bg-red-50/50 p-3">
                <p className="text-[10px] font-semibold uppercase tracking-wide text-gray-500">{t('tradeDetail.metrics.stopLoss')}</p>
                <p className="text-lg font-bold text-gray-900 mt-1">{formatNumber(trade.stop_loss, trade.currency)}</p>
                <p className="text-[11px] text-red-600 mt-1">
                  {slDistancePct != null ? t('tradeDetail.metrics.slDistance', { value: slDistancePct.toFixed(2) }) : '—'}
                </p>
              </div>
              <div className="rounded-lg border border-green-100 bg-green-50/60 p-3">
                <p className="text-[10px] font-semibold uppercase tracking-wide text-gray-500">{t('tradeDetail.metrics.takeProfit')}</p>
                <p className="text-lg font-bold text-green-800 mt-1">{formatNumber(currentTrade.take_profit, trade.currency)}</p>
                <p className="text-[11px] text-gray-500 mt-1">{t('tradeDetail.metrics.plannedRr', { value: trade.rr ?? '—' })}</p>
              </div>
              <div className="rounded-lg border border-purple-100 bg-purple-50/50 p-3">
                <p className="text-[10px] font-semibold uppercase tracking-wide text-gray-500">{t('tradeDetail.metrics.positionSize')}</p>
                <p className="text-lg font-bold text-purple-800 mt-1">{formatNumber(currentTrade.position_size, trade.currency)}</p>
                <p className="text-[11px] text-gray-500 mt-1">{t('tradeDetail.metrics.quantity', { value: currentTrade.quantity ?? '—' })}</p>
              </div>
              <div className="rounded-lg border border-amber-100 bg-amber-50/50 p-3">
                <p className="text-[10px] font-semibold uppercase tracking-wide text-gray-500">{t('tradeDetail.metrics.riskAtStop')}</p>
                <p className="text-lg font-bold text-amber-900 mt-1">
                  {currentRiskAmount != null ? formatNumber(currentRiskAmount, trade.currency) : '—'}
                </p>
                <p className="text-[11px] text-amber-800 mt-1">
                  {currentRiskPct != null ? t('tradeDetail.metrics.riskPct', { value: currentRiskPct.toFixed(1) }) : t('tradeDetail.metrics.riskPctUnknown')}
                </p>
              </div>
              {!currentTrade.exit_date && currentTrade.current_price != null && (
                <div className="rounded-lg border border-sky-100 bg-sky-50/60 p-3">
                  <p className="text-[10px] font-semibold uppercase tracking-wide text-gray-500">{t('tradeDetail.metrics.lastPrice')}</p>
                  <p className="text-lg font-bold text-gray-900 mt-1">{formatNumber(currentTrade.current_price, trade.currency)}</p>
                  <p className="text-[11px] text-gray-500 mt-1">
                    {currentTrade.daily_change_percentage != null
                      ? `${currentTrade.daily_change_percentage >= 0 ? '+' : ''}${currentTrade.daily_change_percentage.toFixed(2)}% ${t('tradeDetail.metrics.today')}`
                      : (currentTrade.current_price_updated_at ? formatDate(currentTrade.current_price_updated_at) : '—')}
                  </p>
                </div>
              )}
              <div className={`rounded-lg border p-3 ${
                currentTrade.exit_date
                  ? (currentTrade.pnl != null && currentTrade.pnl >= 0 ? 'border-green-100 bg-green-50/60' : 'border-red-100 bg-red-50/50')
                  : 'border-gray-100 bg-gray-50/80'
              }`}>
                <p className="text-[10px] font-semibold uppercase tracking-wide text-gray-500">
                  {currentTrade.exit_date ? t('tradeDetail.metrics.resultPnl') : t('tradeDetail.metrics.unrealizedPnl')}
                </p>
                <p className={`text-lg font-bold mt-1 ${
                  currentTrade.exit_date
                    ? (currentTrade.pnl != null && currentTrade.pnl >= 0 ? 'text-green-700' : 'text-red-600')
                    : (currentTrade.unrealized_pnl != null && currentTrade.unrealized_pnl >= 0 ? 'text-green-700' : 'text-red-600')
                }`}>
                  {formatNumber(
                    currentTrade.exit_date ? currentTrade.pnl : (currentTrade.unrealized_pnl ?? null),
                    trade.currency
                  )}
                </p>
                <p className="text-[11px] text-gray-600 mt-1">
                  {currentTrade.exit_date
                    ? (pnlPercentage != null ? `${pnlPercentage >= 0 ? '+' : ''}${pnlPercentage.toFixed(2)}%` : '—')
                    : (currentTrade.unrealized_pnl_percentage != null
                      ? `${currentTrade.unrealized_pnl_percentage >= 0 ? '+' : ''}${currentTrade.unrealized_pnl_percentage.toFixed(2)}%`
                      : '—')}
                  {' · '}
                  {realizedRR != null ? `${realizedRR >= 0 ? '+' : ''}${realizedRR.toFixed(2)}R` : (currentTrade.exit_date ? '—' : t('tradeDetail.metrics.rrOpen'))}
                </p>
              </div>
            </div>
          </div>

          <div className="mt-6 border border-gray-200 rounded-xl p-4 md:p-5 bg-gray-50/50">
            <h3 className="text-lg font-semibold text-gray-900">{t('tradeDetail.priceChart.title')}</h3>
            <p className="text-xs text-gray-500 mt-1 mb-4">{t('tradeDetail.priceChart.subtitle')}</p>
            <TradeDetailChart
              rows={chartRows}
              annotations={chartAnnotations}
              stopSegments={slTpSegments.stop}
              takeProfitSegments={slTpSegments.takeProfit}
              entryY={averageCost}
              currency={currentTrade.currency}
              candleSource={chartSource}
              loading={chartLoading}
            />
          </div>

          {/* Images Accordion */}
          <div className="mt-6 border border-gray-200 rounded-xl overflow-hidden bg-gray-50">
            <button
              onClick={() => setShowImages(!showImages)}
              className="w-full px-4 py-3 flex items-center justify-between bg-white border-b border-gray-100 hover:bg-gray-50 transition-colors"
            >
              <p className="text-sm font-semibold text-gray-800">Setup Images</p>
              <div className="flex items-center gap-2">
                <span className="text-xs text-gray-500">
                  {trade.entry_setup_image || trade.exit_setup_image ? 'View Images' : 'No Images'}
                </span>
                <svg
                  className={`w-5 h-5 text-gray-500 transition-transform ${showImages ? 'transform rotate-180' : ''}`}
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                </svg>
              </div>
            </button>
            {showImages && (
              <div className="p-4">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  {['Entry Setup', 'Exit Setup'].map((label, idx) => {
                    const imageUrl = idx === 0 ? trade.entry_setup_image : trade.exit_setup_image
                    const placeholder = idx === 0 ? 'Entry setup görseli yok' : 'Exit setup görseli yok'
                    return (
                      <div key={label} className="border border-gray-200 rounded-xl overflow-hidden bg-white">
                        <div className="px-4 py-3 flex items-center justify-between bg-gray-50 border-b border-gray-100">
                          <p className="text-sm font-semibold text-gray-800">{label}</p>
                          <span className="text-xs text-gray-500">{imageUrl ? 'Attached' : 'Placeholder'}</span>
                        </div>
                        {imageUrl ? (
                          <img 
                            src={imageUrl} 
                            alt={label} 
                            className="w-full h-56 object-cover cursor-pointer hover:opacity-90 transition-opacity" 
                            onClick={() => setSelectedImage(imageUrl)}
                          />
                        ) : (
                          <div className="h-56 flex items-center justify-center text-sm text-gray-500">
                            {placeholder}
                          </div>
                        )}
                      </div>
                    )
                  })}
                </div>
              </div>
            )}
          </div>

          <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mt-4">
            <div className="p-4 rounded-xl border border-gray-200">
              <p className="text-xs text-gray-500">Quantity</p>
              <p className="text-lg font-semibold text-gray-900">{currentTrade.quantity ?? '-'}</p>
            </div>
            <div className="p-4 rounded-xl border border-gray-200">
              <p className="text-xs text-gray-500">Contract Size</p>
              <p className="text-lg font-semibold text-gray-900">{currentTrade.contract_size ?? '-'}</p>
            </div>
            <div className="p-4 rounded-xl border border-gray-200">
              <p className="text-xs text-gray-500">Exit Price</p>
              <p className="text-lg font-semibold text-gray-900">
                {formatNumber(currentTrade.exit_price, currentTrade.currency)}
              </p>
              <p className="text-xs text-gray-500 mt-1">{formatDate(currentTrade.exit_date)}</p>
            </div>
            <div className="p-4 rounded-xl border border-gray-200">
              <p className="text-xs text-gray-500">P&L</p>
              <p
                className={`text-lg font-semibold ${
                  currentTrade.pnl !== null && currentTrade.pnl >= 0 ? 'text-green-700' : 'text-red-600'
                }`}
              >
                {formatNumber(currentTrade.pnl, currentTrade.currency)}
              </p>
            </div>
          </div>

          {trade.trade_setup && (
            <div className="mt-6">
              <p className="text-xs text-gray-500 uppercase">Setup</p>
              <p className="text-base text-gray-900 mt-1">{trade.trade_setup}</p>
            </div>
          )}

          {trade.notes && (
            <div className="mt-4">
              <p className="text-xs text-gray-500 uppercase">Notes</p>
              <p className="text-base text-gray-900 mt-1 whitespace-pre-wrap">{trade.notes}</p>
            </div>
          )}
        </section>

        <section className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          <div className="lg:col-span-2 bg-app-box border border-gray-200 rounded-2xl shadow-sm p-6">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-semibold text-gray-900">Trade History</h3>
              <div className="flex items-center gap-2">
                {historyAllowed && tradeId && !isTradeClosed && (
                  <button
                    onClick={() => setShowAddEventModal(true)}
                    className="px-3 py-1.5 bg-orange-600 hover:bg-orange-700 text-white text-sm font-medium rounded-lg transition-colors"
                  >
                    Add Event
                  </button>
                )}
                {historyAllowed && tradeId && isTradeClosed && (
                  <span className="px-3 py-1.5 bg-gray-200 text-gray-500 text-sm font-medium rounded-lg cursor-not-allowed">
                    Trade Closed
                  </span>
                )}
                <span className="text-xs text-gray-500">
                  {historyAllowed ? `${historyTimelineVisible.length} events` : 'Pro feature'}
                </span>
              </div>
            </div>
            {!historyAllowed ? (
              <TradeHistoryUpgradeCard />
            ) : historyLoading ? (
              <div className="text-sm text-gray-500">Loading history...</div>
            ) : (
              <div className="space-y-4">
                {historyTimelineVisible.map((item, idx) => (
                  <div key={item.id ?? `${item.date}-${idx}`} className="flex gap-3">
                    <div className="flex flex-col items-center">
                      <div className="w-3 h-3 rounded-full bg-orange-500 mt-1" />
                      {idx !== historyTimelineVisible.length - 1 && <div className="flex-1 w-px bg-gray-200" />}
                    </div>
                    <div className="flex-1 pb-4 border-b border-gray-100 last:border-b-0 last:pb-0">
                      <p className="text-xs text-gray-500">{formatDate(item.date)}</p>
                      <div className="flex items-start justify-between gap-3 mt-1">
                        <div className="flex items-center gap-2">
                          <p className="text-base font-semibold text-gray-900">{item.title}</p>
                          {item.tag && (
                            <span className="px-2 py-0.5 rounded-full text-[11px] bg-gray-100 text-gray-700">
                              {item.tag}
                            </span>
                          )}
                        </div>
                        {historyAllowed &&
                          tradeId &&
                          item.id &&
                          !item.autoGenerated &&
                          item.eventType !== 'market_data' &&
                          item.tag !== 'market_data' && (
                          <button
                            type="button"
                            onClick={() => openEditEvent(item)}
                            className="text-xs text-gray-500 hover:text-orange-600 transition-colors"
                            disabled={editSubmitting}
                          >
                            Edit
                          </button>
                        )}
                      </div>
                      <p className="text-sm text-gray-700 mt-1 whitespace-pre-wrap">{item.description}</p>
                    </div>
                  </div>
                ))}
                {historyError && <p className="text-sm text-red-600">{historyError}</p>}
              </div>
            )}
          </div>

          <div className="bg-app-box border border-gray-200 rounded-2xl shadow-sm p-6">
            <div className="flex items-center justify-between mb-3">
              <h3 className="text-lg font-semibold text-gray-900">Comments</h3>
              <div className="flex items-center gap-2">
                {tradeId && (
                  <button
                    onClick={() => setShowAddCommentModal(true)}
                    className="px-3 py-1.5 bg-orange-600 hover:bg-orange-700 text-white text-sm font-medium rounded-lg transition-colors"
                  >
                    Add Comment
                  </button>
                )}
                <span className="text-xs text-gray-500">{comments.length} entries</span>
              </div>
            </div>
            {commentsLoading ? (
              <div className="text-sm text-gray-500">Loading comments...</div>
            ) : (
              <div className="space-y-3">
                {comments.length === 0 ? (
                  <div className="text-center py-8 text-gray-500 text-sm">
                    No comments yet. Add your first comment!
                  </div>
                ) : (
                  comments.map((comment, idx) => (
                    <div key={`${comment.author}-${idx}`} className="p-3 rounded-xl bg-gray-50 border border-gray-100">
                      <div className="flex items-center justify-between">
                        <p className="text-sm font-semibold text-gray-900">{comment.author}</p>
                        <span className="text-xs text-orange-600">{comment.mood}</span>
                      </div>
                      <p className="text-xs text-gray-500">{formatDate(comment.date)}</p>
                      <p className="text-sm text-gray-800 mt-2">{comment.text}</p>
                    </div>
                  ))
                )}
              </div>
            )}
          </div>
        </section>
        </>
        )}
      </main>

      {/* Add Event Modal */}
      {showAddEventModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl shadow-xl max-w-lg w-full max-h-[90vh] overflow-y-auto">
            <div className="p-6 border-b border-gray-200">
              <div className="flex items-center justify-between">
                <h2 className="text-xl font-semibold text-gray-900">Add Event</h2>
                <button
                  onClick={() => setShowAddEventModal(false)}
                  className="text-gray-400 hover:text-gray-600 transition-colors"
                >
                  <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
              </div>
            </div>

            <form onSubmit={handleAddEvent} className="p-6 space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Event Type <span className="text-red-500">*</span>
                </label>
                <select
                  value={eventForm.event_type}
                  onChange={(e) => setEventForm({ ...eventForm, event_type: e.target.value })}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-transparent"
                  required
                >
                  {eventTypes.map((type) => (
                    <option key={type.value} value={type.value}>
                      {type.label}
                    </option>
                  ))}
                </select>
              </div>

              {showEventDateField && (
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    {showExitFields ? 'Exit Date' : 'Event Date'} <span className="text-red-500">*</span>
                  </label>
                  <LocalizedDateTimeInput
                    value={eventForm.event_date}
                    onChange={(val) => setEventForm({ ...eventForm, event_date: val })}
                    required
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-transparent"
                  />
                </div>
              )}

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  {showQuantityField ? 'Description (Auto)' : showExitFields ? 'Note (Auto)' : 'Description'} <span className="text-red-500">*</span>
                </label>
                <textarea
                  value={showQuantityField ? buildAutoDescriptionForAddReduce() : showExitFields ? buildExitDescription() : eventForm.description}
                  onChange={(e) => setEventForm({ ...eventForm, description: e.target.value })}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-transparent resize-none"
                  rows={4}
                  placeholder="Describe what happened..."
                  required
                  disabled={showQuantityField || showExitFields}
                />
                {showQuantityField && (
                  <p className="text-xs text-gray-500 mt-1">
                    This sentence is generated automatically. Quantity will not be included.
                  </p>
                )}
                {showExitFields && (
                  <p className="text-xs text-gray-500 mt-1">
                    Position closed at price on date with profit/loss %.
                  </p>
                )}
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  {showExitFields ? 'Exit Price' : showQuantityField ? 'Price' : 'Price'} {showQuantityField || showExitFields ? <span className="text-red-500">*</span> : '(Optional)'}
                </label>
                <input
                  type="number"
                  step="any"
                  value={eventForm.price}
                  onChange={(e) => setEventForm({ ...eventForm, price: e.target.value })}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-transparent"
                  placeholder={showExitFields ? 'Exit price' : 'Enter price if relevant'}
                  required={showQuantityField || showExitFields}
                />
              </div>

              {showExitFields && (
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Exit Setup Image URL (Optional)
                  </label>
                  <input
                    type="url"
                    value={eventForm.exit_setup_image}
                    onChange={(e) => setEventForm({ ...eventForm, exit_setup_image: e.target.value })}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-transparent"
                    placeholder="https://example.com/image.png"
                  />
                </div>
              )}

              {showQuantityField && (
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Quantity <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="number"
                    step="any"
                    value={eventForm.quantity}
                    onChange={(e) => setEventForm({ ...eventForm, quantity: e.target.value })}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-transparent"
                    placeholder={eventForm.event_type === 'add' ? 'Quantity to add' : 'Quantity to reduce'}
                    required
                  />
                  <p className="text-xs text-gray-500 mt-1">
                    Current quantity: {currentTrade.quantity ?? 'N/A'}
                  </p>
                </div>
              )}

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Mood (Optional)
                </label>
                <input
                  type="text"
                  value={eventForm.mood}
                  onChange={(e) => setEventForm({ ...eventForm, mood: e.target.value })}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-transparent"
                  placeholder="e.g., Confident, Anxious, Focused"
                />
              </div>

              <div className="flex gap-3 pt-4">
                <button
                  type="button"
                  onClick={() => setShowAddEventModal(false)}
                  className="flex-1 px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors"
                  disabled={submitting}
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="flex-1 px-4 py-2 bg-orange-600 hover:bg-orange-700 text-white rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                  disabled={submitting}
                >
                  {submitting ? 'Adding...' : 'Add Event'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Edit Event Modal */}
      {showEditEventModal && editingEvent && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl shadow-xl max-w-lg w-full max-h-[90vh] overflow-y-auto">
            <div className="p-6 border-b border-gray-200">
              <div className="flex items-center justify-between">
                <h2 className="text-xl font-semibold text-gray-900">Edit Event</h2>
                <button
                  onClick={() => {
                    setShowEditEventModal(false)
                    setEditingEvent(null)
                  }}
                  className="text-gray-400 hover:text-gray-600 transition-colors"
                >
                  <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
              </div>
              <p className="text-xs text-gray-500 mt-1">
                {editingEvent.title} • {formatDate(editingEvent.date)}
              </p>
              {editingEvent.quantity !== null && editingEvent.quantity !== undefined && (
                <p className="text-xs text-gray-500 mt-1">Quantity: {editingEvent.quantity}</p>
              )}
            </div>

            <form onSubmit={handleUpdateEvent} className="p-6 space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Description <span className="text-red-500">*</span>
                </label>
                <textarea
                  value={editEventForm.description}
                  onChange={(e) => setEditEventForm({ ...editEventForm, description: e.target.value })}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-transparent resize-none"
                  rows={4}
                  placeholder="Describe what happened..."
                  required
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Price (Optional)</label>
                <input
                  type="number"
                  step="any"
                  value={editEventForm.price}
                  onChange={(e) => setEditEventForm({ ...editEventForm, price: e.target.value })}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-transparent"
                  placeholder="Enter price if relevant"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Mood (Optional)</label>
                <input
                  type="text"
                  value={editEventForm.mood}
                  onChange={(e) => setEditEventForm({ ...editEventForm, mood: e.target.value })}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-transparent"
                  placeholder="e.g., Confident, Anxious, Focused"
                />
              </div>

              <div className="flex gap-3 pt-4">
                <button
                  type="button"
                  onClick={() => {
                    setShowEditEventModal(false)
                    setEditingEvent(null)
                  }}
                  className="flex-1 px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors"
                  disabled={editSubmitting}
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="flex-1 px-4 py-2 bg-orange-600 hover:bg-orange-700 text-white rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                  disabled={editSubmitting}
                >
                  {editSubmitting ? 'Saving...' : 'Save Changes'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Add Comment Modal */}
      {showAddCommentModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl shadow-xl max-w-lg w-full max-h-[90vh] overflow-y-auto">
            <div className="p-6 border-b border-gray-200">
              <div className="flex items-center justify-between">
                <h2 className="text-xl font-semibold text-gray-900">Add Comment</h2>
                <button
                  onClick={() => setShowAddCommentModal(false)}
                  className="text-gray-400 hover:text-gray-600 transition-colors"
                >
                  <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
              </div>
            </div>

            <form onSubmit={handleAddComment} className="p-6 space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Comment <span className="text-red-500">*</span>
                </label>
                <textarea
                  value={commentForm.text}
                  onChange={(e) => setCommentForm({ ...commentForm, text: e.target.value })}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-transparent resize-none"
                  rows={6}
                  placeholder="Write your thoughts, reflections, or notes about this trade..."
                  required
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Mood (Optional)
                </label>
                <input
                  type="text"
                  value={commentForm.mood}
                  onChange={(e) => setCommentForm({ ...commentForm, mood: e.target.value })}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-transparent"
                  placeholder="e.g., Confident, Reflective, Frustrated, Excited"
                />
              </div>

              <div className="flex gap-3 pt-4">
                <button
                  type="button"
                  onClick={() => setShowAddCommentModal(false)}
                  className="flex-1 px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors"
                  disabled={commentSubmitting}
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="flex-1 px-4 py-2 bg-orange-600 hover:bg-orange-700 text-white rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                  disabled={commentSubmitting}
                >
                  {commentSubmitting ? 'Adding...' : 'Add Comment'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Image Pop-up Modal */}
      {selectedImage && (
        <div 
          className="fixed inset-0 bg-black bg-opacity-75 flex items-center justify-center z-50 p-4"
          onClick={() => setSelectedImage(null)}
        >
          <div className="relative max-w-7xl max-h-[90vh] w-full h-full flex items-center justify-center">
            <button
              onClick={() => setSelectedImage(null)}
              className="absolute top-4 right-4 text-white hover:text-gray-300 transition-colors z-10 bg-black bg-opacity-50 rounded-full p-2"
            >
              <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
            <img 
              src={selectedImage} 
              alt="Enlarged view" 
              className="max-w-full max-h-full object-contain rounded-lg"
              onClick={(e) => e.stopPropagation()}
            />
          </div>
        </div>
      )}
    </div>
  )
}

export default TradeDetail

